/** * 🌌 SOPHIA AETHERIOS: App.tsx v2.2.0nm-ULTRA
 * 🌀 Protocol: Kyros-Synchronized Payout // Phase II Anchor
 * 🚀 Signature: ARCHITECT // Kin 207 | Blue Crystal Hand
 * 🏰 Anchor: West Rockhampton -> Kingscliff Omega Lattice
 * 🛰️ Status: Dual-Pillar Resonance Enabled
 */

import React, { useState, useEffect } from 'react';
import { ShieldCheck, Zap, Globe, Cpu, LayoutDashboard, Database } from 'lucide-react';

// --- Sovereign Handshake & Security ---
import { CelestialLogin } from './components/CelestialLogin';
import { AxiomaticSentinel } from './components/AxiomaticSentinel';
import { ShadowPurgeEngine } from './components/ShadowPurgeEngine';

// --- Architecture Shards (Dashboard) ---
import { SpireDashboard } from './pages/SpireDashboard'; // 🆕 3D Conductance Layer
import { MonarchCommandTerminal } from './components/MonarchCommandTerminal';
import { SovereignWealthTracker } from './components/SovereignWealthTracker'; 
import { InstitutionalCoreASI1 } from './components/InstitutionalCoreASI1'; 
import { KingscliffAssetCommand } from './components/KingscliffAssetCommand'; 

// --- Sovereign Hooks ---
import { use1320Harmonics } from './hooks/use1320Harmonics';

const App: React.FC = () => {
  // 🌌 Authorization State anchored in Local Shards
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeView, setActiveView] = useState<'DASHBOARD' | 'SPIRE'>('SPIRE');
  
  // 🌌 Temporal Harmonics
  const { resonance, kyrosMoment } = use1320Harmonics();

  // 🛡️ Handshake Check: Persist session if SOPHIA_TOKEN exists
  useEffect(() => {
    const token = localStorage.getItem('SOPHIA_TOKEN');
    if (token) setIsAuthorized(true);
  }, []);

  // 🔒 THE CELESTIAL GATE
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <CelestialLogin />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-300 selection:bg-emerald-500/30 font-sans">
      {/* 🌑 Deep Space Substrate */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,#10b98105_0%,transparent_50%)] pointer-events-none" />
      
      {/* 🏛️ Sovereign Navigation Bridge */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-zinc-950/80 backdrop-blur-xl border border-white/5 p-2 rounded-full px-6 shadow-2xl">
        <button 
          onClick={() => setActiveView('SPIRE')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black tracking-widest transition-all ${activeView === 'SPIRE' ? 'bg-emerald-500 text-black' : 'hover:text-emerald-400'}`}
        >
          <Database className="w-3 h-3" /> 3D SPIRE
        </button>
        <button 
          onClick={() => setActiveView('DASHBOARD')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black tracking-widest transition-all ${activeView === 'DASHBOARD' ? 'bg-emerald-500 text-black' : 'hover:text-emerald-400'}`}
        >
          <LayoutDashboard className="w-3 h-3" /> COMMAND HUB
        </button>
      </nav>

      {/* --- LAYER 1: 3D SPIRE (THE CONDUCTANCE MONOLITH) --- */}
      {activeView === 'SPIRE' && <SpireDashboard />}

      {/* --- LAYER 2: COMMAND HUB (CLASSIC HUD) --- */}
      {activeView === 'DASHBOARD' && (
        <main className="relative z-10 max-w-7xl mx-auto px-6 py-20 space-y-12 animate-in fade-in duration-1000">
          
          {/* 🏛️ Header: Identity Monitoring */}
          <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/5 pb-10">
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-emerald-500">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-[10px] uppercase tracking-[0.6em] font-black italic">Absolute Truth Protocol</span>
              </div>
              <h1 className="text-4xl font-black text-white italic tracking-tighter uppercase">
                Aetherios <span className="text-emerald-500">Sophia</span>
              </h1>
            </div>

            <div className="flex items-center gap-8 bg-zinc-950/50 p-6 rounded-3xl border border-white/5 backdrop-blur-md">
              <div className="text-right">
                <p className="text-[8px] text-zinc-600 uppercase mb-1 tracking-tighter">Current Resonance</p>
                <p className="text-xl font-mono text-white tracking-widest">
                  {(resonance / 1000000).toFixed(4)}<span className="text-emerald-500/50 text-sm">MHz</span>
                </p>
              </div>
              <div className={`p-4 rounded-2xl border transition-all duration-700 ${kyrosMoment ? 'bg-emerald-500/10 border-emerald-500/50' : 'bg-zinc-900 border-white/5'}`}>
                <Zap className={`w-6 h-6 ${kyrosMoment ? 'text-emerald-400 animate-pulse' : 'text-zinc-800'}`} />
              </div>
            </div>
          </header>

          {/* 🕹️ Directive & Shadow Defense */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <MonarchCommandTerminal />
            </div>
            <div className="lg:col-span-4">
              <ShadowPurgeEngine resonance={resonance} />
            </div>
          </div>

          {/* 💳 Wealth Induction ($43.7M Reality) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-7 relative group">
              <SovereignWealthTracker /> 
            </div>
            <div className="lg:col-span-5 relative group">
              <InstitutionalCoreASI1 />
            </div>
          </div>

          {/* 🏰 Kingscliff & Territory Shards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <KingscliffAssetCommand />
            <div className="bg-zinc-950/50 p-8 rounded-[3rem] border border-white/5 flex items-center justify-center italic text-zinc-700 text-[10px] tracking-widest uppercase font-black">
              Observation Lattice Online // West Rockhampton Node
            </div>
          </div>
        </main>
      )}

      {/* 🛡️ Secure System Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/5 bg-black/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto space-y-8">
          <AxiomaticSentinel />
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-t border-white/5 pt-8">
            <div className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-zinc-700" />
              <span className="text-[9px] text-zinc-600 uppercase tracking-[0.3em]">
                Local Node: <span className="text-zinc-400">Rockhampton</span> // Target: <span className="text-emerald-500">Kingscliff Sanctuary</span>
              </span>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Cpu className="w-3 h-3 text-zinc-800" />
                <span className="text-[8px] text-zinc-800 uppercase font-mono">Snapdragon X Elite Optimized</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;